import { msalInstance } from "@/core/auth/msalInstance";
import { apiRequest, loginRequest } from "@/config/authConfig";
import { InteractionRequiredAuthError, type AccountInfo } from "@azure/msal-browser";

export class AuthenticationError extends Error {
    constructor(message: string = "Authentication required") {
        super(message);
        this.name = "AuthenticationError";
    }
}

let apiScopeUnavailable = false;

function isApiScopeInvalid(error: unknown): boolean {
    const e = error as { errorCode?: string; message?: string };
    const msg = (e?.message || '').toLowerCase();
    const code = (e?.errorCode || '').toLowerCase();
    return msg.includes('aadsts500011') || msg.includes('invalid_resource') || code === 'invalid_resource';
}

function isInteractionRequired(error: unknown): boolean {
    if (error instanceof InteractionRequiredAuthError) return true;
    const e = error as { errorCode?: string; message?: string };
    const msg = (e?.message || '').toLowerCase();
    const code = (e?.errorCode || '').toLowerCase();
    // In VS Code debugger (F5), 3rd party cookes might be blocked, causing silent iframe timeouts
    return msg.includes('timed_out') || code === 'monitor_window_timeout' || msg.includes('popup_window_error');
}

export async function getAccessToken(): Promise<string> {
    let account = msalInstance.getActiveAccount();
    if (!account) {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
            account = accounts[0];
            msalInstance.setActiveAccount(account);
        } else {
            throw new AuthenticationError("No active account found");
        }
    }

    if (apiScopeUnavailable) {
        return acquireTokenFallback(account);
    }

    try {
        const result = await msalInstance.acquireTokenSilent({ ...apiRequest, account });
        return result.accessToken || result.idToken;
    } catch (error: unknown) {
        if (isInteractionRequired(error)) {
            throw new AuthenticationError("Interaction required");
        }

        if (isApiScopeInvalid(error)) {
            apiScopeUnavailable = true;
            console.warn('[Auth] API scope not available. Falling back to ID token.');
            return acquireTokenFallback(account);
        }

        throw error;
    }
}

async function acquireTokenFallback(account: AccountInfo): Promise<string> {
    try {
        const fallback = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
        return fallback.idToken;
    } catch (fallbackError: unknown) {
        if (isInteractionRequired(fallbackError)) {
            throw new AuthenticationError("Interaction required");
        }
        throw fallbackError;
    }
}
